/**
 * Roof Scanner Plugin Logger
 * Browser-to-server logging using Pino for Google Cloud Logging integration
 *
 * All log levels (info, warn, error) are transmitted to roof-server
 * roof-server forwards logs to Google Cloud Logging via stdout (k8s captures)
 * No console output in browser - logs go to cloud via backend
 */

import pino from 'pino';

// Helper to get serverUrl from widget config (injected at runtime)
function getServerUrl() {
  if (typeof window !== 'undefined' && window.ROOF_SCANNER_CONFIG?.serverUrl) {
    return window.ROOF_SCANNER_CONFIG.serverUrl;
  }
  // No fallback - serverUrl is required
  return null;
}

// Initialize Pino logger with backend transmission
const logger = pino({
  name: 'roof-scanner-plugin',
  level: 'info', // Capture info, warn, and error levels
  browser: {
    asObject: true,
    transmit: {
      send: function(level, logEvent) {
        const serverUrl = getServerUrl();

        // Skip if serverUrl not configured
        if (!serverUrl) {
          return;
        }

        // Get JWT token from config
        const token = window.ROOF_SCANNER_CONFIG?.widgetToken;

        // Skip if token not available yet (widget still initializing)
        if (!token) {
          return;
        }

        // Extract data from Pino's log event structure
        const context = {
          ...logEvent.bindings,
          level: level.value,
          time: logEvent.ts
        };

        // Send to backend endpoint with JWT authentication (fail silently to not break widget)
        fetch(`${serverUrl}/api/v1/roof-scanner/logs`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            level: level.label?.toLowerCase() || 'error', // 'info', 'warn', 'error'
            message: logEvent.messages[0] || '',
            context: context,
            source: 'roof-scanner-plugin',
            timestamp: new Date().toISOString()
          })
        }).catch(() => {
          // Silent failure - don't break widget if logging fails
        });
      }
    }
  }
});

/**
 * Standard field names for consistent error tracking
 * Use these constants instead of hardcoding field names
 *
 * Naming convention:
 * - Constant: UPPERCASE_SNAKE_CASE
 * - Value: camelCase string
 */
const FIELDS = {
  // Widget context
  API_KEY: 'apiKey',
  ORG_ID: 'organizationId',

  // Page context
  PAGE_NUMBER: 'pageNumber',

  // Location & Building
  BUILDING_ID: 'buildingId',
  ADDRESS: 'address',

  // Job Processing
  JOB_ID: 'jobId',
  JOB_STATUS: 'jobStatus',

  // Business context (aligned with roof-server)
  LEAD_ID: 'leadId',
  QUOTE_ID: 'quoteId',

  // Errors (primary use case)
  ERROR: 'err',
  ERROR_MESSAGE: 'errorMessage',
  ERROR_CODE: 'errorCode',

  // HTTP & APIs
  ENDPOINT: 'endpoint',
  STATUS_CODE: 'statusCode',

  // Operations
  OPERATION: 'operation',
  REASON: 'reason',

  // Roof Face Rendering
  ROOF_FACE_INDEX: 'roofFaceIndex',
  IS_SELECTED: 'isSelected',
  PIXELS_RENDERED: 'pixelsRendered'
};

export { logger, FIELDS };
